"use client";

import { useTranslation } from "react-i18next";
import React, { useRef } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import ContainerGlob from "../../../../componentsGLob/ContainerGlob";
import ComparisonTable from "./TableComparative";
import { Button } from "@packages/ui/src/components/shadcn/main-button";

const cards = [
  {
    src: "/card/worker.svg",
    altKey: "workerAlt",
    shadow: "shadow-blue-300/30",
    bgColor: "bg-[#39a8e0]",
  },
  {
    src: "/card/forager.svg",
    altKey: "foragerAlt",
    shadow: "shadow-red-300/30",
    bgColor: "bg-[#e8501d]",
  },
  {
    src: "/card/queen.svg",
    altKey: "queenAlt",
    shadow: "shadow-amber-300/30",
    bgColor: "bg-[#f9b131]",
  },
];

const transforms = [
  {
    opacity: "opacity-50",
    classes: "rotate-12 translate-x-6 translate-y-4 skew-y-3",
  },
  {
    opacity: "opacity-70",
    classes: "rotate-6 translate-x-3 translate-y-2 skew-y-1",
  },
];

export default function BuzzCardEffect() {
  const { t } = useTranslation("buzzCard");
  const tableRef = useRef<HTMLDivElement | null>(null);

  const scrollToTable = () => {
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.15,
        duration: 0.6,
        ease: "easeInOut" as const,
      },
    }),
  };

  return (
    <section
      className="w-full bg-gradient-to-b from-yellow-50 to-white py-20 px-6 text-center"
      id="piani"
    >
      <ContainerGlob>
        <h2 className="text-3xl md:text-4xl font-semibold mb-6">
          {t("programTitle")}
        </h2>
        <div className="text-sm text-gray-600 max-w-md mx-auto mb-16 leading-relaxed">
          <p>{t("introLine1")}</p>
          <p>{t("introLine2")}</p>
        </div>

        {/* Card Grid */}
        <div className="flex flex-wrap justify-center items-stretch gap-x-12 gap-y-12 sm:gap-x-16 sm:gap-y-16 lg:gap-x-24 lg:gap-y-20 xl:gap-x-32 xl:gap-y-24">
          {cards.map(({ src, altKey, shadow, bgColor }, index) => (
            <motion.div
              key={index}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={cardVariants}
              className="relative w-48 sm:w-56 aspect-[3/4] transform-gpu perspective-1000"
              style={{ padding: "0 8px" }}
            >
              {transforms.map(({ opacity, classes }, i) => (
                <div
                  key={i}
                  className={`absolute rounded-xl w-full h-full ${opacity} ${shadow} ${bgColor} transform ${classes}`}
                />
              ))}
              <Image
                src={src}
                alt={t(altKey)}
                fill
                className="object-contain rounded-xl transform transition-all duration-300 hover:scale-105 hover:-rotate-1"
                sizes="(max-width: 640px) 192px, 224px"
                priority
              />
            </motion.div>
          ))}
        </div>

        <div className="mt-20 flex justify-center">
          <Button
            className="bg-amber-500 text-white font-semibold uppercase tracking-wide rounded-lg shadow-lg px-8 py-4 hover:bg-amber-600"
            onClick={scrollToTable}
            variant="default"
            size="lg"
            effect="shineHover"
          >
            {t("discoverMore")}
          </Button>
        </div>

        {/* Comparison Table */}
        <div ref={tableRef} className="mt-24">
          <ComparisonTable />
        </div>
      </ContainerGlob>
    </section>
  );
}
